import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';

const STORAGE_KEY = 'lemoh:age-verified';

/**
 * A 21+ age gate. Blocks the site on the first page load of a session until the
 * visitor confirms their age.
 *
 * Markup renders hidden; the inline script in snippets/age-gate.liquid reveals it
 * before first paint when the visitor hasn't confirmed yet (so verified visitors
 * never see a flash, and the content is never visible behind an unconfirmed gate).
 * This component owns the behaviour: scroll lock, focus trap, and the two choices.
 *
 * - confirm(): stores a session flag and fades the gate out.
 * - deny(): swaps to a terminal "must be 21+" panel; the gate stays up, no way back.
 *
 * @extends {Component}
 */
class AgeGateComponent extends Component {
  requiredRefs = ['panel', 'question', 'denied'];

  connectedCallback() {
    super.connectedCallback();

    if (this.#verified) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    lockScroll(this);
    trapFocus(this.refs.panel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    removeTrapFocus();
    unlockScroll(this);
  }

  get #verified() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  /** Visitor is 21+: remember for the session and release the gate. */
  confirm() {
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // Private mode / storage disabled — gate won't persist across pages, but still passes here.
    }

    removeTrapFocus();
    unlockScroll(this);

    this.setAttribute('data-closing', '');

    const remove = () => this.remove();
    this.addEventListener(
      'transitionend',
      (event) => {
        if (event.target === this) remove();
      },
      { once: true }
    );
    // Failsafe if the transition never fires (reduced motion collapses it to ~1ms).
    setTimeout(remove, 600);
  }

  /** Visitor is under 21: swap to the terminal message and keep the gate locked. */
  deny() {
    removeTrapFocus();
    this.refs.question.hidden = true;
    this.refs.denied.hidden = false;
    this.refs.panel.focus();
  }
}

if (!customElements.get('age-gate-component')) {
  customElements.define('age-gate-component', AgeGateComponent);
}
